import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import { handleApiError } from '../lib/errors';
import {
  type WbsDetails,
  type WbsDetailsInput,
  wbsDetailsService,
} from '../services/wbsDetailsService';
import { logger } from '../utils/logger';

type FormData = WbsDetailsInput;

const emptyForm: FormData = {
  id: '',
  wbsNoLevel5: '',
  wbsNameLevel5: '',
  wbsNoLevel6: '',
  wbsNameLevel6: '',
};

function rowToForm(row: WbsDetails): FormData {
  return {
    id: row.id ?? '',
    wbsNoLevel5: row.wbsNoLevel5 ?? '',
    wbsNameLevel5: row.wbsNameLevel5 ?? '',
    wbsNoLevel6: row.wbsNoLevel6 ?? '',
    wbsNameLevel6: row.wbsNameLevel6 ?? '',
  };
}

function formToPayload(
  form: FormData,
  isEdit: boolean
): WbsDetailsInput | Omit<WbsDetailsInput, 'id'> {
  const trim = (v: string) => v.trim();
  const fields = {
    wbsNoLevel5: trim(form.wbsNoLevel5 ?? '') || undefined,
    wbsNameLevel5: trim(form.wbsNameLevel5 ?? '') || undefined,
    wbsNoLevel6: trim(form.wbsNoLevel6 ?? '') || undefined,
    wbsNameLevel6: trim(form.wbsNameLevel6 ?? '') || undefined,
  };
  if (isEdit) {
    return fields;
  }
  return {
    id: trim(form.id ?? ''),
    ...fields,
  };
}

interface WbsDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (row: WbsDetails) => void;
  onEntrySaved?: () => void;
  row?: WbsDetails | null;
  isEdit?: boolean;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

export const WbsDetailsDrawer: React.FC<WbsDetailsDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onEntrySaved,
  row,
  isEdit = false,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if ((isEdit || readOnly) && row) {
        setFormData(rowToForm(row));
      } else {
        setFormData(emptyForm);
      }
      setSubmitError(null);
    }
  }, [isOpen, isEdit, readOnly, row]);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const payload = formToPayload(formData, isEdit);
      const response =
        isEdit && row
          ? await wbsDetailsService.update(row.id, payload as Omit<WbsDetailsInput, 'id'>)
          : await wbsDetailsService.create(payload as WbsDetailsInput);
      onSave(response);
      onEntrySaved?.();
      onClose();
    } catch (error) {
      logger.error('Error saving WBS detail:', error);
      setSubmitError(handleApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly;
  const idDisabled = fieldsDisabled || isEdit;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View WBS Detail' : isEdit ? 'Edit WBS Detail' : 'Add WBS Detail'}
      description={
        readOnly
          ? 'WBS level 5 and 6 details.'
          : isEdit
            ? 'Update the WBS detail below.'
            : 'Fill in the WBS detail fields.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update' : 'Add'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        <TextFormField
          label="ID"
          value={formData.id ?? ''}
          onChange={(v) => handleChange('id', v)}
          disabled={idDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="WBS No Level 5"
          value={formData.wbsNoLevel5 ?? ''}
          onChange={(v) => handleChange('wbsNoLevel5', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="WBS Name Level 5"
          value={formData.wbsNameLevel5 ?? ''}
          onChange={(v) => handleChange('wbsNameLevel5', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="WBS No Level 6"
          value={formData.wbsNoLevel6 ?? ''}
          onChange={(v) => handleChange('wbsNoLevel6', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
        <TextFormField
          label="WBS Name Level 6"
          value={formData.wbsNameLevel6 ?? ''}
          onChange={(v) => handleChange('wbsNameLevel6', v)}
          disabled={fieldsDisabled}
          copyable={readOnly}
        />
      </FieldGroup>
    </FormDetailPane>
  );
};
